import {
  ComponentRef,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  ViewContainerRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { DraftFieldIndicatorComponent } from '../../shared/draft-field-indicator/draft-field-indicator.component';
import { InkPoint, InkStroke } from '../models/draft-form.model';
import { DraftPolicyService } from '../services/draft-policy.service';
import { DraftStateService } from '../services/draft-state.service';
import { simplifyStroke } from '../utils/rdp-simplify';
import { generateSvgPath, renderStrokeOnCanvas } from '../utils/svg-path.util';

const STROKE_COLOR = '#000000';
const STROKE_WIDTH = 2;
const POINT_EPSILON = 0.003;
const INTERSECTION_MARGIN = '200px 0px 200px 0px';
const SVG_NS = 'http://www.w3.org/2000/svg';

// Inline pen cursor (16x16 SVG encoded as data URI)
const PEN_CURSOR_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24'%3E%3Cpath d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z' fill='%23333'/%3E%3C/svg%3E") 2 18, crosshair`;

type FieldState = 'IDLE' | 'VISIBLE' | 'DRAWING';

/**
 * Per-field ink overlay directive.
 * Applied to <input> and <textarea> elements.
 * Uses @Optional() DraftStateService — no-op when service is not provided
 * (i.e. in normal edit mode outside draft route).
 */
// eslint-disable-next-line @angular-eslint/directive-selector
@Directive({ selector: '[drawableField]' })
export class DrawableFieldDirective implements OnInit, OnDestroy {
  @Input('drawableField') fieldId: string;
  @Input() drawableFieldReadonly = false;
  @Input() drawableFieldTitle: string;

  private state: FieldState = 'IDLE';
  private wrapperEl: HTMLElement;
  private svgEl: SVGSVGElement;
  private canvasEl: HTMLCanvasElement;
  private canvasCtx: CanvasRenderingContext2D;

  private activeStroke: InkStroke | null = null;
  private strokeStartTime = 0;
  private needsRender = false;
  private rafId: number | null = null;

  private intersectionObserver: IntersectionObserver;
  private resizeObserver: ResizeObserver;
  private strokesSub: Subscription;
  private indicatorRef: ComponentRef<DraftFieldIndicatorComponent>;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private ngZone: NgZone,
    private viewContainerRef: ViewContainerRef,
    @Optional() private draftPolicy: DraftPolicyService,
    @Optional() private draftState: DraftStateService
  ) {}

  ngOnInit(): void {
    if (!this.fieldId) {
      return;
    }

    // Draft mode: full drawing functionality
    if (this.draftState) {
      if (this.drawableFieldTitle) {
        this.draftState.setFieldTitle(this.fieldId, this.drawableFieldTitle);
      }
      this.initDraftMode();
      return;
    }

    // Normal edit mode: show indicator icon if ink data exists
    this.initIndicatorMode();
  }

  ngOnDestroy(): void {
    this.indicatorRef?.destroy();

    if (!this.draftState) {
      return;
    }

    this.stopRenderLoop();
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.strokesSub?.unsubscribe();
    this.removeCanvas();
    this.removeSvgOverlay();
  }

  private initDraftMode(): void {
    // Remove placeholder text so the drawing surface is clean
    this.renderer.removeAttribute(this.el.nativeElement, 'placeholder');

    this.createWrapper();
    this.setupIntersectionObserver();
    this.setupResizeObserver();

    // Subscribe to stroke changes for re-rendering
    this.strokesSub = this.draftState
      .getStrokes$(this.fieldId)
      .subscribe(() => this.onStrokesChanged());
  }

  private initIndicatorMode(): void {
    this.indicatorRef = this.viewContainerRef.createComponent(
      DraftFieldIndicatorComponent
    );
    this.indicatorRef.instance.fieldId = this.fieldId;

    // Place indicator on the left side of the form field.
    // In LTR fields (dir="ltr") that's the prefix; in RTL fields it's the suffix.
    const host = this.el.nativeElement;
    const formField = host.closest('mat-form-field') as HTMLElement;
    if (formField) {
      const indicatorEl = this.indicatorRef.location.nativeElement;
      const isLtr =
        formField.getAttribute('dir') === 'ltr' ||
        !!formField.closest('[dir="ltr"]');
      const containerClass = isLtr
        ? 'mat-form-field-prefix'
        : 'mat-form-field-suffix';

      let container = formField.querySelector(
        '.' + containerClass
      ) as HTMLElement;
      if (!container) {
        container = this.renderer.createElement('div');
        this.renderer.addClass(container, containerClass);
        const infix = formField.querySelector('.mat-form-field-infix');
        if (infix?.parentElement) {
          if (isLtr) {
            this.renderer.insertBefore(infix.parentElement, container, infix);
          } else {
            this.renderer.appendChild(infix.parentElement, container);
          }
        }
      }
      this.renderer.insertBefore(container, indicatorEl, container.firstChild);
    }
  }

  // --- DOM Setup ---

  private createWrapper(): void {
    const host = this.el.nativeElement;

    // Use the closest mat-form-field as overlay container (covers label + suffix),
    // or fall back to the host input/textarea itself
    const container = (host.closest('mat-form-field') as HTMLElement) || host;

    // Make it a positioning context for the absolute SVG/canvas overlays
    this.renderer.setStyle(container, 'position', 'relative');
    this.renderer.setStyle(
      container,
      'background-color',
      'rgba(24, 144, 255, 0.05)'
    );

    this.wrapperEl = container;
  }

  // --- IntersectionObserver (IDLE ↔ VISIBLE) ---

  private setupIntersectionObserver(): void {
    this.ngZone.runOutsideAngular(() => {
      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && this.state === 'IDLE') {
            this.state = 'VISIBLE';
            this.renderSvgOverlay();
          } else if (!entry.isIntersecting && this.state === 'VISIBLE') {
            this.state = 'IDLE';
            this.removeSvgOverlay();
          }
        },
        { rootMargin: INTERSECTION_MARGIN }
      );
      this.intersectionObserver.observe(this.wrapperEl);
    });
  }

  // --- ResizeObserver ---

  private setupResizeObserver(): void {
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.state === 'VISIBLE') {
          this.renderSvgOverlay();
        }
      });
      this.resizeObserver.observe(this.el.nativeElement);
    });
  }

  // --- SVG Overlay (VISIBLE state - lightweight) ---

  private renderSvgOverlay(): void {
    if (!this.svgEl) {
      this.svgEl = document.createElementNS(SVG_NS, 'svg') as SVGSVGElement;
      this.svgEl.setAttribute('viewBox', '0 0 1 1');
      this.svgEl.setAttribute('preserveAspectRatio', 'none');
      this.svgEl.style.cssText =
        'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:auto;z-index:9;touch-action:none;';

      // Pen cursor for drawable zones (unless readonly)
      if (!this.drawableFieldReadonly) {
        this.svgEl.style.cursor = PEN_CURSOR_SVG;
      }

      this.wrapperEl.appendChild(this.svgEl);
      this.svgEl.addEventListener('pointerdown', this.onPointerDown);
    }

    // Clear existing paths
    while (this.svgEl.firstChild) {
      this.svgEl.removeChild(this.svgEl.firstChild);
    }

    // Render strokes as SVG paths
    const strokes = this.draftState.getStrokes(this.fieldId);
    strokes.forEach((stroke) => {
      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', generateSvgPath(stroke.points));
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', STROKE_COLOR);
      path.setAttribute('stroke-width', String(STROKE_WIDTH));
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      path.setAttribute('vector-effect', 'non-scaling-stroke');
      this.svgEl.appendChild(path);
    });
  }

  private removeSvgOverlay(): void {
    if (this.svgEl) {
      this.svgEl.removeEventListener('pointerdown', this.onPointerDown);
      this.svgEl.remove();
      this.svgEl = null;
    }
  }

  // --- Canvas (DRAWING state - active drawing) ---

  private switchToCanvas(): void {
    this.removeSvgOverlay();

    const rect = this.wrapperEl.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    this.canvasEl = this.renderer.createElement('canvas');
    this.canvasEl.style.cssText = `position:absolute;top:0;left:0;width:100%;height:100%;z-index:10;touch-action:none;cursor:${PEN_CURSOR_SVG};`;

    // Physical pixels (sharp rendering)
    this.canvasEl.width = rect.width * dpr;
    this.canvasEl.height = rect.height * dpr;

    // Avoid { desynchronized: true } — on some touch devices it causes the
    // canvas to initialise with opaque-black pixels instead of transparent.
    this.canvasCtx = this.canvasEl.getContext('2d');
    this.canvasCtx.scale(dpr, dpr);

    // Ensure the canvas is transparent before it becomes visible
    this.canvasCtx.clearRect(0, 0, rect.width, rect.height);

    this.wrapperEl.appendChild(this.canvasEl);

    // Add pointer event listeners on canvas
    this.canvasEl.addEventListener('pointermove', this.onPointerMove);
    this.canvasEl.addEventListener('pointerup', this.onPointerUp);
    this.canvasEl.addEventListener('pointerleave', this.onPointerUp);
    this.canvasEl.addEventListener('pointercancel', this.onPointerUp);
  }

  private removeCanvas(): void {
    if (this.canvasEl) {
      this.canvasEl.removeEventListener('pointermove', this.onPointerMove);
      this.canvasEl.removeEventListener('pointerup', this.onPointerUp);
      this.canvasEl.removeEventListener('pointerleave', this.onPointerUp);
      this.canvasEl.removeEventListener('pointercancel', this.onPointerUp);
      this.canvasEl.remove();
      this.canvasEl = null;
      this.canvasCtx = null;
    }
  }

  private switchToSvg(): void {
    this.removeCanvas();
    this.state = 'VISIBLE';
    this.renderSvgOverlay();
  }

  // --- Pointer Events ---

  private onPointerDown = (event: PointerEvent): void => {
    if (this.drawableFieldReadonly || !this.draftPolicy.shouldDraw(event)) {
      return; // Let the event propagate normally (scroll, tap, etc.)
    }

    event.preventDefault();
    event.stopPropagation();

    this.state = 'DRAWING';
    this.switchToCanvas();

    // Capture pointer
    this.canvasEl.setPointerCapture(event.pointerId);

    // Start new stroke
    this.strokeStartTime = performance.now();
    this.activeStroke = {
      id: uuidv4(),
      points: [this.normalizePoint(event)],
      timestamp: Date.now()
    };

    this.needsRender = true;
    this.startRenderLoop();
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.activeStroke) {
      return;
    }

    const point = this.normalizePoint(event);

    // Distance-based downsampling: skip if too close to last point
    const last = this.activeStroke.points[this.activeStroke.points.length - 1];
    const dist = Math.hypot(point.x - last.x, point.y - last.y);
    if (dist < POINT_EPSILON) {
      return;
    }

    this.activeStroke.points.push(point);
    this.needsRender = true;
  };

  private onPointerUp = (): void => {
    if (!this.activeStroke) {
      return;
    }

    this.stopRenderLoop();

    // Simplify stroke (RDP)
    this.activeStroke.points = simplifyStroke(this.activeStroke.points);

    // Commit to service (re-enter zone for state update)
    const stroke = this.activeStroke;
    this.activeStroke = null;

    this.ngZone.run(() => {
      this.draftState.addStroke(this.fieldId, stroke);
    });

    // Switch back to SVG
    this.switchToSvg();
  };

  // --- Coordinate Normalization ---

  private normalizePoint(event: PointerEvent): InkPoint {
    const rect = this.canvasEl.getBoundingClientRect();
    const x = Math.max(
      0,
      Math.min(1, (event.clientX - rect.left) / rect.width)
    );
    const y = Math.max(
      0,
      Math.min(1, (event.clientY - rect.top) / rect.height)
    );

    const point: InkPoint = { x, y };

    // Capture pressure if available
    if (event.pressure > 0 && event.pressure < 1) {
      point.p = event.pressure;
    }

    // Relative time since stroke start
    point.t = Math.round(performance.now() - this.strokeStartTime);

    return point;
  }

  // --- Render Loop (runs outside NgZone) ---

  private startRenderLoop(): void {
    if (this.rafId !== null) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.renderFrame();
    });
  }

  private renderFrame(): void {
    if (!this.canvasCtx) {
      return;
    }

    if (this.needsRender) {
      const rect = this.wrapperEl.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;

      this.canvasCtx.clearRect(0, 0, w, h);

      const render = (s: InkStroke) =>
        renderStrokeOnCanvas(
          this.canvasCtx,
          s,
          w,
          h,
          STROKE_COLOR,
          STROKE_WIDTH
        );

      this.draftState.getStrokes(this.fieldId).forEach(render);
      if (this.activeStroke) {
        render(this.activeStroke);
      }

      this.needsRender = false;
    }

    this.rafId = requestAnimationFrame(() => this.renderFrame());
  }

  private stopRenderLoop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  // --- Stroke Change Handler ---

  private onStrokesChanged(): void {
    if (this.state === 'VISIBLE') {
      this.renderSvgOverlay();
    }
    // If DRAWING, the render loop handles it
  }
}
